import { BUTTON_LABELS } from '@/constants';
import { cn } from '@/utils';
import { Icon } from '@/components/Icons';
import { scoreRowCardStyles } from '../ScoreRowCard.styles';
import React from 'react';

/** Props for the ScoreRowCardToggleButton component */
export interface ScoreRowCardToggleButtonProps {
  /** Label shown when the section is collapsed */
  collapseLabel: string;
  /** Label shown when the section is expanded */
  expandLabel?: string;
  /** Whether the section is currently expanded */
  isExpanded?: boolean;
  /** Whether the card is in a forced-expanded state (hides toggle) */
  forceExpanded?: boolean;
  /** Whether there is more feedback available beyond the visible set */
  hasMoreFeedback?: boolean;
  /** Whether the data is still loading */
  isLoading?: boolean;
  /** Callback when the toggle button is clicked */
  onToggle?: () => void;
  /** Whether the toggle button should be shown */
  showToggle?: boolean;
}

/**
 * Renders the expand/collapse toggle button for the ScoreRowCard
 */
export const ScoreRowCardToggleButton: React.FC<ScoreRowCardToggleButtonProps> = ({
  collapseLabel = BUTTON_LABELS.SHOW_LESS,
  expandLabel = BUTTON_LABELS.SHOW_MORE,
  forceExpanded,
  hasMoreFeedback,
  isExpanded,
  isLoading,
  onToggle,
  showToggle,
}) => {
  const iconClassNames = cn(
    'text-neutral-gray-600',
    'transition-transform duration-200',
    { 'rotate-180': isExpanded },
  );

  if (showToggle && hasMoreFeedback && !forceExpanded && !isLoading) {
    return (
      <button
        aria-expanded={isExpanded}
        aria-label={isExpanded ? collapseLabel : expandLabel}
        className={scoreRowCardStyles.toggleButton}
        onClick={onToggle}
      >
        <span className={scoreRowCardStyles.toggleText}>
          {isExpanded ? collapseLabel : expandLabel}
        </span>
        <Icon className={iconClassNames} name="CaretDown" size={12} />
      </button>
    );
  }

  return null;
};

ScoreRowCardToggleButton.displayName = 'ScoreRowCardToggleButton';

export default ScoreRowCardToggleButton;
